import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import StudentLayout from "./StudentLayout";
import { Link } from "react-router-dom";

function ClassworkPage() {
  const { currentUser } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [completionMap, setCompletionMap] = useState({});
  const [openFolders, setOpenFolders] = useState({});

  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchData = async () => {
      const [assignSnap, resSnap, progressSnap, folderSnap] = await Promise.all([
        getDocs(collection(db, "assignments")),
        getDocs(collection(db, "lessonResources")),
        getDocs(collection(db, "assignmentProgress")),
        getDocs(collection(db, "lessonFolders")),
      ]);

      const progressMapData = {};
      const completionData = {};

      progressSnap.docs.forEach((doc) => {
        const data = doc.data();
        if (data.studentUID === currentUser.uid) {
          progressMapData[data.assignmentId] = true;
          if (data.questionResults && data.questionResults.every((r) => r !== null)) {
            completionData[data.assignmentId] = true;
          }
        }
      });
      setProgressMap(progressMapData);
      setCompletionMap(completionData);

      const folders = folderSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(folder => folder.studentUID === currentUser.uid);

        const assignments = assignSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(a => a.isDraft !== false);
      

        const folderLessons = folders.map(folder => {
          const assignmentIds = folder.assignments || [];
const linkedAssignments = assignmentIds
  .map(id => assignments.find(a => a.id === id))
  .filter(Boolean);
          return {
            folder,
            assignments: linkedAssignments,
          };
        });
        

      // ✅ Sort by most recent createdAt (descending)
      folderLessons.sort((a, b) => new Date(b.folder.createdAt) - new Date(a.folder.createdAt));

      setLessons(folderLessons);
    };

    fetchData();
  }, [currentUser]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) : "—";

    const toggleFolder = (folderId) => {
      setOpenFolders(prev => ({
        ...prev,
        [folderId]: !prev[folderId], // only toggle the clicked one
      }));
    };
    

  return (
    <StudentLayout>
      <style>
        {`
          @keyframes flash {
            0% { box-shadow: 0 0 0px gold; }
            100% { box-shadow: 0 0 15px 5px gold; }
          }
        `}
      </style>

      <div style={styles.container}>
        <h1 style={styles.heading}>📚 Classwork</h1>

        {lessons.length === 0 ? (
          <p>No classwork found 🎉</p>
        ) : (
          <ul style={{ ...styles.list, justifyContent: "flex-start" }}>
            {lessons.map((lesson, i) => {
              const { folder, assignments } = lesson;
              const isCurrent = i === 0;

              return (
                <li
                  key={folder.id}
                  style={{
                    ...styles.card,
                    width: "260px",
                    fontSize: "0.9rem",
                    ...(isCurrent ? styles.currentLesson : {}),
                  }}
                >
                  <div
                    onClick={() => toggleFolder(folder.id)}
                    style={{
                      cursor: "pointer",
                      marginBottom: openFolders[folder.id] ? "1rem" : "0",
                    }}
                  >
                    <h3 style={{ ...styles.title, fontSize: "1rem" }}>📖 {folder.title}</h3>
                    <p style={{ ...styles.date, fontSize: "0.8rem" }}>📅 {formatDate(folder.createdAt)}</p>
                  </div>

                  {openFolders[folder.id] && (
                    <div style={styles.cardContent}>
                      {folder.notes && <p style={styles.assignment}>{folder.notes}</p>}

                      {folder.googleResources?.length > 0 && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <strong>📎 Resources:</strong>
                          <ul>
                            {folder.googleResources.map((res, idx) => (
                              <li key={idx}>
                                <a href={res.url} target="_blank" rel="noreferrer">
                                  {res.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {assignments.length > 0 &&
                        assignments.map((a) => {
                          const completed = completionMap[a.id];
                          const inProgress = progressMap[a.id];

                          let label = "Start";
                          let linkPath = `/student-assignment/${a.id}`;
                          if (completed) {
                            label = "Review";
                            linkPath += "?review=true";
                          } else if (inProgress) {
                            label = "Continue";
                          }

                          return (
                            <Link
                              key={a.id}
                              to={linkPath}
                              style={{
                                textDecoration: "none",
                                color: "#0984e3",
                                display: "block",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  backgroundColor: "#f8f9ff",
                                  borderRadius: "20px",
                                  padding: "1.2rem",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                                  borderLeft: "6px solid #5cb85c",
                                  marginTop: "1rem",
                                }}
                              >
                                <span style={{ fontWeight: "500", fontSize: "1rem", lineHeight: "1.4" }}>
                                  {a.title}
                                </span>
                              </div>
                            </Link>
                          );
                        })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </StudentLayout>
  );
}

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#fefae0",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1.5rem",
    color: "#2d3436",
  },
  list: {
    listStyleType: "none",
    padding: 0,
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
    justifyContent: "flex-start",
    alignItems: "flex-start", // 👈 this line fixes the issue
  },
  
  card: {
    background: "linear-gradient(to bottom right, #ffffff, #f0f4ff)",
    borderRadius: "12px",
    padding: "1rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
  },
  currentLesson: {
    border: "3px solid gold",
    animation: "flash 1.5s infinite alternate",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#2d3436",
    marginBottom: "0.5rem",
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.4rem",
    marginBottom: "0.5rem",
  },
  tag: {
    padding: "0.3rem 0.6rem",
    borderRadius: "8px",
    fontSize: "0.8rem",
    fontWeight: "500",
    color: "#2d3436",
  },
  date: {
    fontSize: "0.9rem",
    marginBottom: "0.8rem",
    color: "#636e72",
  },
  assignment: {
    backgroundColor: "#dfe6e9",
    padding: "0.6rem",
    borderRadius: "8px",
    fontSize: "0.85rem",
    lineHeight: "1.4",
    marginBottom: "1rem",
  },
  buttonContainer: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "0.5rem",
  },
  button: {
    padding: "0.4rem 0.8rem",
    fontSize: "0.85rem",
    fontWeight: "600",
    border: "none",
    borderRadius: "20px",
    backgroundColor: "#00b894",
    color: "white",
    cursor: "pointer",
    textDecoration: "none",
    textAlign: "center",
  },
};

export default ClassworkPage;
